import * as React from 'react'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { ContractTaxonomyFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import i18n from 'modules/core/lib/i18n'

import type Employee from 'modules/employees/store/models/Employee'
import DeleteModal from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/List/Line/Form/DeleteModal'
import type SupplementForm from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/SupplementForm'
import type SupplementsFormStore from 'modules/payroll_bundle/components/Handoffs/Supplements/Forms/store'
import type SupplementObservationForm from 'modules/payroll_bundle/lib/SupplementObservationForm'
import { Option, PAID, PAYROLL_CALCULATION } from 'modules/payroll_bundle/lib/constants'

import Modal from './SupplementObservationModal'

interface Props {
  employee: Employee | undefined
  supplementObservationForm: SupplementObservationForm
  supplementForm: SupplementForm
  onSupplementFormSubmit: () => Promise<unknown>
  store: SupplementsFormStore
  taxonomies: ContractTaxonomyFragment[]
  onDelete: () => Promise<unknown>
  isSupplementReadOnly: boolean
  policyPeriodStatus: string
  hover: boolean
  isObservationModalOpen: boolean
  openNoteModal: () => void
  closeNoteModal: () => void
}

const SupplementPopoverMenu = ({
  employee,
  supplementObservationForm,
  supplementForm,
  onSupplementFormSubmit,
  store,
  taxonomies,
  onDelete,
  isSupplementReadOnly,
  policyPeriodStatus,
  hover,
  isObservationModalOpen,
  closeNoteModal,
  openNoteModal,
}: Props) => {
  const [openPopover, setOpenPopover] = React.useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)

  const observationAvailable = !!supplementObservationForm.get('text').originalValue

  const canViewObservations = employee?.policy.can('readUserPayrollObservations')

  const options: Option[] = []
  const supplementIsCreated = !!supplementForm.fields.contracts_taxonomy_id?.value
  if (!(isSupplementReadOnly || [PAYROLL_CALCULATION, PAID].includes(policyPeriodStatus))) {
    options.push({
      onClick: () => {
        setDeleteModalOpen(true)
      },
      title: i18n.t('handoffs.supplements.delete_supplement_action'),
      icon: 'delete',
      danger: true,
    })
  }
  if (canViewObservations && supplementIsCreated) {
    options.unshift({
      onClick: openNoteModal,
      title: observationAvailable
        ? i18n.t('handoffs.supplements.edit_note')
        : i18n.t('handoffs.supplements.create_note'),
      icon: 'comment',
      danger: false,
    })
  }

  if (!options.length) return null

  const showDeleteButton = (optionsObject: Option[]): boolean => {
    if (optionsObject.length === 1) {
      const option = optionsObject[0]
      if (option) {
        return option.icon === 'delete'
      }
    }
    return false
  }

  const displayDeleteButton = showDeleteButton(options)

  const DeleteButton = () => (
    <Button
      type="secondary"
      onClick={() => setDeleteModalOpen(true)}
      iconName="delete"
      aria-label={i18n.t('common.delete')}
    />
  )

  const EllipsisButton = () => (
    <Button type="secondary" onClick={() => setOpenPopover(!openPopover)} iconName="ellipsis" />
  )

  const renderButton = () => {
    if (hover) {
      return displayDeleteButton ? (
        <DeleteButton />
      ) : (
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <EllipsisButton />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {options.map(({ title, onClick, icon, danger }, index) => (
              <PopoverMenu.TouchableOption
                key={index}
                title={title}
                onClick={onClick}
                icon={icon}
                danger={danger}
              />
            ))}
          </PopoverMenu.Content>
        </PopoverMenu>
      )
    } else {
      return displayDeleteButton ? (
        <DeleteButton />
      ) : (
        <Box aria-label={i18n.t('payroll_bundle.supplements.supplement_menu_aria_label')}>
          <EllipsisButton />
        </Box>
      )
    }
  }

  return (
    <Box aria-label={i18n.t('payroll_bundle.supplements.supplement_menu_aria_label')}>
      {renderButton()}

      {isObservationModalOpen && (
        <Modal
          onClose={closeNoteModal}
          form={supplementObservationForm}
          employee={employee}
          supplementForm={supplementForm}
          onSupplementFormSubmit={onSupplementFormSubmit}
          store={store}
          taxonomies={taxonomies}
        />
      )}
      {deleteModalOpen && (
        <DeleteModal
          onClose={() => setDeleteModalOpen(false)}
          onDelete={() => {
            ;(onDelete(), setDeleteModalOpen(false))
          }}
        />
      )}
    </Box>
  )
}

export default observer(SupplementPopoverMenu)
